// Live presence for the reply box: guest typing / online, plus "Continue on WhatsApp" bridge when the guest has left.
import React, { useEffect, useRef, useState } from 'react';
import { ExternalLink, Loader2, MessageCircle, X } from 'lucide-react';
import { apiCall } from './_shared';

export const usePresence = (sessionId) => {
  const [p, setP] = useState(null);
  useEffect(() => {
    if (!sessionId) return undefined;
    let alive = true;
    const tick = () => apiCall(`/api/admin/chat-live/presence/${sessionId}`).then((d) => alive && setP(d)).catch(() => {});
    tick();
    const id = setInterval(tick, 2500);
    return () => { alive = false; clearInterval(id); };
  }, [sessionId]);
  return p;
};

export const useStaffTypingPing = (sessionId) => {
  const last = useRef(0);
  return () => {
    if (!sessionId || Date.now() - last.current < 3000) return;
    last.current = Date.now();
    apiCall(`/api/admin/chat-sessions/${sessionId}/typing`, { method: 'POST' }).catch(() => {});
  };
};

export const PresenceLine = ({ presence }) => {
  if (!presence) return null;
  if (presence.guest_typing) {
    return <span className="text-xs text-emerald-700 italic inline-flex items-center gap-1" data-testid="conversation-guest-typing"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> guest is typing…</span>;
  }
  if (presence.guest_online) {
    return <span className="text-xs text-emerald-700 inline-flex items-center gap-1" data-testid="conversation-guest-online"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> guest is on the site</span>;
  }
  return <span className="text-xs text-slate-400 inline-flex items-center gap-1" data-testid="conversation-guest-offline"><span className="w-1.5 h-1.5 rounded-full bg-slate-300" /> guest left the site{presence.guest_seen_at ? ` · last seen ${new Date(presence.guest_seen_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}</span>;
};

export const WhatsAppBridge = ({ sessionId, presence, draft, onDone }) => {
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');
  const knownPhone = presence?.guest_phone || '';
  useEffect(() => { if (open) setMessage(draft || ''); }, [open, draft]);
  const go = async () => {
    setBusy(true); setErr('');
    try {
      const r = await apiCall(`/api/admin/chat-sessions/${sessionId}/whatsapp-bridge`, { method: 'POST', body: JSON.stringify({ message: message.trim() || 'How can we help you further?', phone: phone.trim() || null }) });
      setResult(r);
      window.open(r.wa_url, '_blank', 'noopener');
      onDone?.();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const suggested = presence && !presence.guest_online && !presence.guest_typing;
  return (
    <div className="inline-flex flex-col items-end gap-1">
      <button type="button" onClick={() => setOpen((v) => !v)} className={`inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2.5 py-1 border ${suggested ? 'bg-green-600 text-white border-green-600 hover:bg-green-700' : 'text-green-700 border-green-300 hover:bg-green-50'}`} title="Continue this conversation on the guest's WhatsApp" data-testid="conversation-whatsapp-bridge">
        <MessageCircle className="w-3.5 h-3.5" /> Continue on WhatsApp{suggested ? ' — guest left' : ''}
      </button>
      {open && (
        <div className="w-full sm:w-[420px] bg-white border border-green-300 rounded-lg p-3 shadow-lg space-y-2 text-left" data-testid="whatsapp-bridge-panel">
          <div className="flex items-center justify-between">
            <span className="text-sm font-bold text-green-800">Continue on WhatsApp</span>
            <button type="button" onClick={() => setOpen(false)} className="text-slate-400 hover:text-red-600" aria-label="Close" data-testid="whatsapp-bridge-close"><X className="w-4 h-4" /></button>
          </div>
          {knownPhone ? (
            <div className="text-xs text-slate-600">Guest number: <b data-testid="whatsapp-bridge-phone">{knownPhone}</b></div>
          ) : (
            <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Guest WhatsApp number, e.g. +49 170 …" className="w-full border rounded px-2 py-1 text-xs" data-testid="whatsapp-bridge-phone-input" />
          )}
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} placeholder="Your message — a short recap of the website chat is added automatically" className="w-full border rounded px-2 py-1 text-xs" data-testid="whatsapp-bridge-message" />
          <div className="flex items-center justify-between gap-2">
            <span className="text-[11px] text-slate-500">Opens WhatsApp with the recap + your message ready to send.</span>
            <button type="button" onClick={go} disabled={busy || (!knownPhone && !phone.trim())} className="text-xs bg-green-600 hover:bg-green-700 text-white font-semibold rounded px-3 py-1.5 disabled:opacity-50 inline-flex items-center gap-1" data-testid="whatsapp-bridge-send">
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <ExternalLink className="w-3 h-3" />} Open WhatsApp
            </button>
          </div>
          {err && <div className="text-xs text-red-600" data-testid="whatsapp-bridge-error">{err}</div>}
          {result && <div className="text-[11px] text-emerald-800" data-testid="whatsapp-bridge-result">Ready for {result.phone}. {result.note || ''} <a href={result.wa_url} target="_blank" rel="noreferrer" className="underline">Open again</a></div>}
        </div>
      )}
    </div>
  );
};
